/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { BaseApplication } from "./base/baseApplication";
import { UnitTest } from "./unit.test";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { WalletconnectModule } from "@vlinder-web/walletconnect-module-react";

import {
  AppNavigatorService,
  BreadcrumbService,
  LandingPageService,
  PrivacyPageService,
  TermsPageService,
  ProfilePageService,
  DashboardOverviewService,
  SignInPageService,
  SignUpPageService,
  ResetPasswordPageService,
  ForgotPasswordPageService,
  CollectionPageService,
  AssetDetailPageService,
    CollectionHomePageService,
} from "../services";

import { DEFAULT_APP_CONFIG } from "../config/default.config";

export class VApplication extends BaseApplication {
  private static instance: VApplication;

  private constructor(options: any = {}) {
    super(options);

    this.setupBindings();

    //Modules
    this.initModules();

    //Services
    this.initServices();
  }

  static getInstance(options: any = {}) {
    if (!this.instance) {
      this.instance = new VApplication(options);
    }
    return this.instance;
  }

  // @ts-ignore
  async start() {
    await this.initRunLevel();
    await this.unitTest();
  }

  setupBindings() {
    //------------->>>>>--------------//

    this.configure("services.SignInPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
    });
    //------------->>>>>--------------//
    this.configure("services.SignUpPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      termsURL: DEFAULT_APP_CONFIG?.organization?.termsURL,
    });
    //------------->>>>>--------------//
    this.configure("services.ForgotPasswordPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
    });
    //------------->>>>>--------------//
    this.configure("services.ResetPasswordPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
    });

    this.configure("services.WalletconnectModule", {
      bridge: DEFAULT_APP_CONFIG.walletConnect?.relayURL,
    });
  }

  async initRunLevel() {
    const storageSrv = StorageModule?.getInstance();
    // const DEFAULT_APP_CONFIG = storageSrv.get("appConfig", "session");
    //------------->>>>>--------------//
    const graphqlModule = this.getSync(
      "services.GraphqlServerModule"
    ) as GraphqlServerModule;
    if (graphqlModule) {
      type TClient = typeof DEFAULT_APP_CONFIG.servers.graphql[0];
      DEFAULT_APP_CONFIG?.servers.graphql.forEach((client: TClient) => {
        graphqlModule
          .createClient(client.name, {
            url: client.url,
            getContext: this.getGraphQLContext.bind(this),
          })
          .then((res) => {
            console.log(`initRunLevel check`, res);
          })
          .catch((err) => {
            console.log(`error in initRunLevel  ${err.message}`);
          });
      });
    }
    //------------->>>>>--------------//
    const storageService = this.getSync(
      "services.StorageModule"
    ) as StorageModule;
    await storageService?.init();
  }

  async unitTest() {
    const ut = new UnitTest(VApplication);
    ut.run();
  }

  initModules() {
    this.service("services.EventBusModule", EventBusModule);
    this.service("services.GraphqlServerModule", GraphqlServerModule);
    this.service("services.WalletconnectModule", WalletconnectModule);
  }

  initServices() {
    this.service("services.AppNavigatorService", AppNavigatorService);
    this.service("services.BreadcrumbService", BreadcrumbService);
    this.service("services.LandingPageService", LandingPageService);
    this.service("services.PrivacyPageService", PrivacyPageService);
    this.service("services.TermsPageService", TermsPageService);
    this.service("services.ProfilePageService", ProfilePageService);
    this.service("services.DashboardOverviewService", DashboardOverviewService);
    this.service("services.SignInPageService", SignInPageService);
    this.service("services.SignUpPageService", SignUpPageService);
    this.service(
      "services.ForgotPasswordPageService",
      ForgotPasswordPageService
    );
    this.service("services.ResetPasswordPageService", ResetPasswordPageService);
    this.service("services.CollectionPageService", CollectionPageService);
    this.service("services.AssetDetailPageService", AssetDetailPageService);
    this.service("services.CollectionHomePageService", CollectionHomePageService);
  }

  private getGraphQLContext() {
    const loginService = this.getSync(
      "services.AppNavigatorService"
    ) as AppNavigatorService;
    let token = loginService?.getToken();
    // let token ="eyJhbGciOiJSUzUxMiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGluZHVzLmlvIiwiaWQiOiI2MzMyY2I5MzNhNTdjOWViYTFjNGQxZWIiLCJyb2xlIjoibWFzdGVyIGFkbWluIiwib3JnSWQiOiI2MzMyY2I5MDNhNTdjOWViYTFjNGQxZTgiLCJpYXQiOjE2NzAyMjY2MjAsImV4cCI6MTY3MDMxMzAyMCwiYXVkIjoiZGV2LWRpZ2ljZXJ0LmtsZWZraS5pbyIsImlzcyI6InZsaW5kZXIiLCJzdWIiOiJhZG1pbkBpbmR1cy5pbyIsImp0aSI6IjdmMmUwNzEwLTYzMjktNDVmNi04ZGZmLTgwYTBjMDhjNmQ5NSJ9.CXCdzhMcDp5IvyseHDneYuIDtZjAT_Gtb6Ibnrmludf-xAzcgUnoGERU7NBVhKLyroxurXr6FFz3jINkQkvpUES6RmDWDwa7v34Jfv9m2G_Nnxf_fnuslo5AShbJSQoDE810AhzLeBN46ze2T0bHDbs6hebJJkqlLEEk2VAIJN8";
    //FIXME:
    if (token) {
      return {
        headers: {
          ...(DEFAULT_APP_CONFIG?.headers || {}),
          authentication: token,
          // ...{ ...deviceInfoService?.getDeviceInfo() },
          // ...{ ...deviceInfoService?.getAppInfo() },
        },
      };
    } else {
      return {
        headers: {
          ...(DEFAULT_APP_CONFIG?.headers || {}),
          // ...{ ...deviceInfoService?.getDeviceInfo() },
          // ...{ ...deviceInfoService?.getAppInfo() },
        },
      };
    }
  }
}
